import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { loadLocalMatchFile } from "../../hooks/useLocalMatchFile";

const STORAGE_KEY = "video_input_device_id";
const SOURCE_MODE_KEY = "video_input_source_mode";

function classifyDevice(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("obs virtual")) return { tag: "OBS", color: "text-amber-400" };
  if (
    l.includes("elgato") ||
    l.includes("cam link") ||
    l.includes("game capture") ||
    l.includes("magewell") ||
    l.includes("avermedia") ||
    l.includes("live gamer") ||
    l.includes("usb capture") ||
    l.includes("hdmi") ||
    l.includes("video capture")
  ) {
    return { tag: "CAPTURE CARD", color: "text-emerald-400" };
  }
  return { tag: "WEBCAM", color: "text-sky-400" };
}

function VideoPlayer({ matchId, onStream, onFileLoad, onFileTimeUpdate, onFileClear }, ref) {
  const videoRef = useRef(null);

  // Expose a captureFrames(n, intervalMs) method to the parent so the AI-suggest
  // flow can pull stills from the current source — works for both the live
  // capture-card stream and an uploaded file.
  useImperativeHandle(ref, () => ({
    async captureFrames(n = 8, intervalMs = 350) {
      const v = videoRef.current;
      if (!v || (v.readyState || 0) < 2) return [];
      const W = 640, H = 360;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      const frames = [];
      const isLive = !!v.srcObject;
      if (isLive) {
        // Live source: snapshot N frames at intervalMs apart from now.
        for (let i = 0; i < n; i++) {
          try { ctx.drawImage(v, 0, 0, W, H); } catch { return frames; }
          frames.push(canvas.toDataURL("image/jpeg", 0.7));
          if (i < n - 1) await new Promise(r => setTimeout(r, intervalMs));
        }
      } else if (v.src) {
        // File source: seek backwards to (current - half-window) then step forward.
        const halfWindowSec = (n - 1) * intervalMs / 1000 / 2;
        const start = Math.max(0, v.currentTime - halfWindowSec);
        const wasPlaying = !v.paused;
        v.pause();
        for (let i = 0; i < n; i++) {
          const t = Math.min((v.duration || start + n), start + i * intervalMs / 1000);
          await new Promise(resolve => {
            const onSeek = () => { v.removeEventListener("seeked", onSeek); resolve(); };
            v.addEventListener("seeked", onSeek);
            v.currentTime = t;
          });
          try { ctx.drawImage(v, 0, 0, W, H); } catch { /* tainted */ continue; }
          frames.push(canvas.toDataURL("image/jpeg", 0.7));
        }
        if (wasPlaying) { try { await v.play(); } catch { /* ignore */ } }
      }
      return frames;
    },
  }));
  const streamRef = useRef(null);
  const onStreamRef = useRef(onStream);
  useEffect(() => { onStreamRef.current = onStream; }, [onStream]);

  // device | file — switch between live capture and a recorded video file
  const [sourceMode, setSourceMode] = useState(
    () => localStorage.getItem(SOURCE_MODE_KEY) || "device",
  );
  useEffect(() => {
    localStorage.setItem(SOURCE_MODE_KEY, sourceMode);
  }, [sourceMode]);

  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [status, setStatus] = useState("idle"); // idle | requesting | live | error
  const [error, setError] = useState("");
  const [trackInfo, setTrackInfo] = useState(null); // {width,height,frameRate,label}

  // File-mode state
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);
  // Bumped by the visibility-change hook below to force the IDB restore
  // effect to re-fire if the tab regains focus and the <video> ended
  // up empty (memory pressure / extension interference / OS swap).
  const [rehydrateTick, setRehydrateTick] = useState(0);

  // Operator-selectable forward/backward step (seconds). Persisted to
  // localStorage so the operator's preference survives reloads. Also
  // governs the keyboard ←/→ shortcut (we intercept those so the browser
  // default — which varies wildly by browser and can be 30s+ on long
  // videos — doesn't fire). 5s is the default; operators pick from the
  // dropdown next to the seek buttons.
  const SEEK_STEPS = [5, 10, 15, 20, 30];
  const SEEK_STEP_KEY = "zcd_video_seek_step";
  const [seekStepSec, setSeekStepSec] = useState(() => {
    const stored = Number(localStorage.getItem(SEEK_STEP_KEY) || 5);
    return SEEK_STEPS.includes(stored) ? stored : 5;
  });
  useEffect(() => {
    try { localStorage.setItem(SEEK_STEP_KEY, String(seekStepSec)); } catch { /* quota */ }
  }, [seekStepSec]);

  // Single seek helper used by both the buttons AND the keyboard handler.
  // Reading the ref keeps the closures simple — every caller picks up the
  // current step automatically.
  const seekStepRef = useRef(seekStepSec);
  useEffect(() => { seekStepRef.current = seekStepSec; }, [seekStepSec]);
  const nudgeBy = (deltaSec) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      const dur = isFinite(v.duration) ? v.duration : Infinity;
      v.currentTime = Math.max(0, Math.min(dur, (v.currentTime || 0) + deltaSec));
    } catch { /* ignore */ }
  };

  // Stable refs so async effects can call latest props without retriggering
  // on every render (and without dependency-array thrash).
  const onFileLoadRef = useRef(onFileLoad);
  useEffect(() => { onFileLoadRef.current = onFileLoad; }, [onFileLoad]);

  // ── Video persistence: file + playback position survive refresh / QC ──
  //
  // The operator picks a match video once. From that moment on:
  //   * the File itself is persisted to IndexedDB via saveLocalMatchFile
  //     (BBB calls this on first pick, triggered by onFileLoad)
  //   * the currentTime is persisted to localStorage every ~1s while
  //     playing, plus on pause / seeked / visibilitychange / pagehide
  //
  // On mount, if matchId + sourceMode==="file", we hydrate from BOTH:
  //   1. Load Blob from IDB → set src + setStatus("live")
  //   2. Read saved currentTime → seek as soon as loadedmetadata fires
  //
  // Tracked via a ref to the freshly-created blob URL so we don't tangle
  // with React state during the cleanup (the prior implementation revoked
  // the URL milliseconds after creation due to a closure-capture bug).
  const restoredUrlRef = useRef(null);
  const POSITION_KEY = matchId ? `zcd_match_video_pos_${matchId}` : null;

  useEffect(() => {
    // Only restore once per (matchId, sourceMode) pair; bail early if we
    // have nothing to restore from or we're already in file mode with a
    // URL bound to the video element.
    if (!matchId || sourceMode !== "file") return undefined;
    if (restoredUrlRef.current) return undefined;
    let cancelled = false;

    loadLocalMatchFile(matchId).then((rec) => {
      if (cancelled || !rec?.file_blob) return;
      const url = URL.createObjectURL(rec.file_blob);
      restoredUrlRef.current = url;
      setFileUrl(url);
      setFileName(rec.file_name || "");

      const v = videoRef.current;
      if (v) {
        v.srcObject = null;
        v.src = url;
        v.setAttribute("controls", "true");
        v.muted = true;
        // Seek to last-saved playhead position once metadata is ready.
        // Doing this on `loadedmetadata` (not immediately) is the only
        // reliable way — currentTime= before metadata arrives is a no-op
        // in most browsers.
        const savedPos = POSITION_KEY
          ? Number(localStorage.getItem(POSITION_KEY) || 0)
          : 0;
        if (savedPos > 0) {
          const seek = () => {
            try { v.currentTime = savedPos; } catch { /* ignore */ }
            v.removeEventListener("loadedmetadata", seek);
          };
          v.addEventListener("loadedmetadata", seek);
        }
      }
      setStatus("live");
      setError("");

      // Re-broadcast to the parent so its fileUpload hook state stays
      // in sync with the restored File. Reconstructing as File() (not
      // just the Blob) keeps the parent's `file.name` accessor working.
      try {
        const restoredFile = new File(
          [rec.file_blob], rec.file_name || "match.mp4",
          { type: rec.file_blob.type || "video/mp4" },
        );
        onFileLoadRef.current?.(restoredFile);
      } catch { /* File() may not be available in some embedded webviews */ }
    }).catch(() => { /* IDB miss → nothing to restore, that's fine */ });

    // IMPORTANT: cleanup does NOT revoke the URL. The URL belongs to the
    // <video> element for as long as the player is mounted. We only flag
    // the in-flight load as cancelled so a slow IDB read can't blast
    // state into an unmounted component. The unmount-cleanup effect
    // below handles the one-shot URL revocation when the component
    // genuinely goes away.
    return () => { cancelled = true; };
    // rehydrateTick is intentionally in deps so the visibility-change
    // hook below can force the restore to re-fire when the tab regains
    // focus and the <video> ended up empty (memory pressure / extension
    // interference / OS swap dropping the blob URL).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, sourceMode, POSITION_KEY, rehydrateTick]);

  // ── Persist playback position ──
  // Track currentTime in a ref that updates on every timeupdate /
  // pause / seeked event. Writing the REF (not videoRef.current at
  // cleanup-time) means we never write a stale 0 when the video
  // element is mid-unmount and its own currentTime has been reset.
  //
  // Throttled write to localStorage every 1s while playing, plus
  // immediate flushes on pause / seeked / visibilitychange (hidden) /
  // pagehide / beforeunload. The effect cleanup also writes — covers
  // SPA route changes where pagehide doesn't fire.
  const lastKnownTimeRef = useRef(0);

  useEffect(() => {
    if (!POSITION_KEY || sourceMode !== "file") return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    let lastWrite = 0;
    const persist = () => {
      const t = lastKnownTimeRef.current;
      if (t > 0 && isFinite(t)) {
        try { localStorage.setItem(POSITION_KEY, String(t)); } catch { /* quota */ }
      }
    };
    const recordTime = () => {
      if (typeof v.currentTime === "number" && isFinite(v.currentTime)) {
        lastKnownTimeRef.current = v.currentTime;
      }
    };
    const onTime = () => {
      recordTime();
      const now = Date.now();
      if (now - lastWrite < 1000) return;
      lastWrite = now;
      persist();
    };
    const onPauseOrSeek = () => { recordTime(); persist(); };
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        recordTime();
        persist();
      }
    };
    const onPageHide = () => { recordTime(); persist(); };

    v.addEventListener("timeupdate", onTime);
    v.addEventListener("pause", onPauseOrSeek);
    v.addEventListener("seeked", onPauseOrSeek);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      // Final synchronous write FIRST while the video element + its
      // currentTime are still valid. The ref already has the latest
      // value anyway, but a last recordTime() snapshot is cheap.
      recordTime();
      persist();
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("pause", onPauseOrSeek);
      v.removeEventListener("seeked", onPauseOrSeek);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [POSITION_KEY, sourceMode, fileUrl]);

  // ── Keyboard ←/→ override ──
  // Native <video controls> handles left/right arrow keys for "small
  // seek", BUT some browsers scale that step with video duration — on a
  // 4-hour match recording Chrome can jump 2+ minutes per press.
  // Operators expect a consistent 5s (or whatever the dropdown is set
  // to). We intercept ←/→ on the video element itself in capture phase
  // and force OUR step. Listener is attached to the <video> element so
  // the existing BBB scoring shortcuts (digits / w / n / b / l / k /
  // arrows for other purposes) aren't affected when the operator is on
  // the scoring keypad — arrows only steal when focus is in the video.
  useEffect(() => {
    if (sourceMode !== "file") return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const onArrow = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // preventDefault FIRST so the browser default seek doesn't fire
      // even on browsers that ignore stopPropagation in capture phase.
      e.preventDefault();
      e.stopPropagation();
      const delta = e.key === "ArrowRight" ? seekStepRef.current : -seekStepRef.current;
      nudgeBy(delta);
    };
    // Capture phase + the listener is on the video itself: when the
    // video has focus (operator clicked into it), our handler runs
    // before the browser's built-in seek logic.
    v.addEventListener("keydown", onArrow, true);
    return () => v.removeEventListener("keydown", onArrow, true);
  }, [sourceMode, fileUrl]);

  // ── Tab-focus re-hydration ──
  // Edge case: the BBB tab stays mounted while the operator pops to a
  // QC tab, but something in the user's browser (memory pressure /
  // extension / OS swap) drops the blob URL from the <video> element.
  // When they return to the BBB tab, this effect notices the visibility
  // change, checks whether the player should HAVE a file but doesn't,
  // and re-runs the IDB restore. Cheap — just clears the "restored"
  // ref and the existing restore effect picks up on the next render.
  useEffect(() => {
    if (!matchId || sourceMode !== "file") return undefined;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const v = videoRef.current;
      // If the <video> has no src AND no fileUrl in state, force the
      // restore effect to re-run by clearing the dedup ref. The next
      // render will see (!restoredUrlRef.current) and load from IDB.
      if (v && !v.src && !fileUrl) {
        restoredUrlRef.current = null;
        // Bump fileUrl to trigger a re-render so the restore effect
        // is re-evaluated. Setting to null is a no-op if already null,
        // so use a separate force-trigger pattern — increment a tick.
        setRehydrateTick((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [matchId, sourceMode, fileUrl]);

  // Stop any active live stream and notify the parent.
  const stopActiveStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      onStreamRef.current?.(null);
    }
  };

  // Step 1 (device mode): probe permission + enumerate
  const refreshDevices = async () => {
    setError("");
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ video: true });
      probe.getTracks().forEach((t) => t.stop());
    } catch (e) {
      setStatus("error");
      setError(
        e.name === "NotAllowedError"
          ? "Camera permission denied. Click the camera icon in the address bar and allow access, then click Refresh."
          : e.name === "NotFoundError"
          ? "No video input devices found. Plug in your capture card / camera and click Refresh."
          : `Camera access failed: ${e.name || e.message || "unknown"}`
      );
      return;
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    const vids = all.filter((d) => d.kind === "videoinput");
    setDevices(vids);
    if (!selectedId && vids.length) {
      const cap = vids.find((d) => classifyDevice(d.label).tag === "CAPTURE CARD");
      const obs = vids.find((d) => classifyDevice(d.label).tag === "OBS");
      const pick = cap || obs || vids[0];
      setSelectedId(pick.deviceId);
    }
  };

  // Enumerate devices when in device mode; clean up on switch to file mode.
  useEffect(() => {
    if (sourceMode !== "device") return;
    refreshDevices();
    const onChange = () => refreshDevices();
    navigator.mediaDevices.addEventListener?.("devicechange", onChange);
    return () => {
      navigator.mediaDevices.removeEventListener?.("devicechange", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode]);

  // Step 2 (device mode): open the chosen device
  useEffect(() => {
    if (sourceMode !== "device") return;
    if (!selectedId) return;
    let cancelled = false;
    setStatus("requesting");
    setError("");

    const open = async () => {
      stopActiveStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedId },
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 60 },
          },
          // Capture broadcast audio (commentary + crowd) with browser DSP off
          // so what reaches the recorder is what the capture card actually
          // received. Preview <video> below stays muted to prevent feedback.
          audio: {
            echoCancellation: false,
            autoGainControl: false,
            noiseSuppression: false,
            channelCount: 2,
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.src = "";
          videoRef.current.removeAttribute("controls");
          videoRef.current.srcObject = stream;
        }
        onStreamRef.current?.(stream);
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        setTrackInfo({
          label: track.label,
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate
            ? Math.round(settings.frameRate * 10) / 10
            : null,
        });
        setStatus("live");
        localStorage.setItem(STORAGE_KEY, selectedId);
      } catch (e) {
        setStatus("error");
        setError(
          e.name === "NotReadableError"
            ? "Device is in use by another app (likely OBS). Close OBS or pick a different device."
            : e.name === "OverconstrainedError"
            ? "Device does not support 1080p60 — falling back to default."
            : `Failed to open device: ${e.name || e.message}`
        );
      }
    };

    open();
    return () => {
      cancelled = true;
    };
  }, [sourceMode, selectedId]);

  // Switching modes: clean up the resources of the OTHER mode.
  useEffect(() => {
    if (sourceMode === "file") {
      stopActiveStream();
      setStatus("idle");
      setTrackInfo(null);
      setError("");
      // Allow the IDB-load effect to re-fire on this mount cycle if we
      // switched device→file in the same session; the ref was previously
      // set when we last had a file URL bound. Reset → IDB-load runs.
      restoredUrlRef.current = null;
    } else {
      // Switching to device — drop file URL + src
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
        setFileUrl(null);
      }
      setFileName("");
      restoredUrlRef.current = null;
      if (videoRef.current) {
        videoRef.current.removeAttribute("controls");
        videoRef.current.src = "";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopActiveStream();
      if (fileUrl) URL.revokeObjectURL(fileUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedLabel = useMemo(() => {
    const d = devices.find((x) => x.deviceId === selectedId);
    return d?.label || "";
  }, [devices, selectedId]);
  const selectedTag = classifyDevice(selectedLabel);

  // ---- File-mode handlers ---------------------------------------------
  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    const url = URL.createObjectURL(f);
    setFileUrl(url);
    setFileName(f.name);
    restoredUrlRef.current = url;  // mark "restore done" so the IDB-load effect doesn't fire over this fresh pick
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = url;
      videoRef.current.setAttribute("controls", "true");
      videoRef.current.muted = true; // allow autoplay; user can unmute
      videoRef.current.play?.().catch(() => {});
    }
    // A NEW file pick resets the saved playhead — the prior file's
    // position is irrelevant. Persistence picks back up from 0 as the
    // operator starts playing.
    if (POSITION_KEY) {
      try { localStorage.removeItem(POSITION_KEY); } catch { /* ignore */ }
    }
    setStatus("live");
    setError("");
    onFileLoad?.(f);
  };

  const clearFile = () => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setFileName("");
    restoredUrlRef.current = null;
    if (videoRef.current) {
      videoRef.current.removeAttribute("controls");
      videoRef.current.src = "";
    }
    setStatus("idle");
    // Drop the saved playback position too — a fresh file load shouldn't
    // resume at the previous file's timestamp.
    if (POSITION_KEY) {
      try { localStorage.removeItem(POSITION_KEY); } catch { /* ignore */ }
    }
    onFileClear?.();
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Source row — Live / Upload toggle + active picker, all on one line.
          flex-wrap kicks in on narrow viewports so nothing clips.            */}
      <div className="flex items-center flex-wrap gap-1.5 text-[10px] uppercase tracking-wider">
        <button
          type="button"
          onClick={() => setSourceMode("device")}
          className={`shrink-0 py-1 px-2 rounded-md border transition-colors ${
            sourceMode === "device"
              ? "border-button-primary bg-button-primary/15 text-text-primary"
              : "border-bg-secondary bg-bg-secondary/40 text-text-secondary hover:text-text-primary"
          }`}
          title="Camera, capture card, or OBS Virtual Camera"
        >
          Live
        </button>
        <button
          type="button"
          onClick={() => setSourceMode("file")}
          className={`shrink-0 py-1 px-2 rounded-md border transition-colors ${
            sourceMode === "file"
              ? "border-button-primary bg-button-primary/15 text-text-primary"
              : "border-bg-secondary bg-bg-secondary/40 text-text-secondary hover:text-text-primary"
          }`}
          title="Pre-recorded match video file (mp4, webm, mkv, mov)"
        >
          Upload from computer
        </button>

        {/* Live mode: device picker inline */}
        {sourceMode === "device" && (
          <>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="flex-1 min-w-[140px] bg-bg-main border border-border-default rounded-md py-1 px-2 text-[10px] normal-case focus:outline-none focus:border-button-primary"
            >
              {devices.length === 0 && <option value="">(no devices found)</option>}
              {devices.map((d) => {
                const { tag } = classifyDevice(d.label);
                return (
                  <option key={d.deviceId} value={d.deviceId}>
                    [{tag}] {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              onClick={refreshDevices}
              className="shrink-0 px-2 py-1 rounded-md border border-border-default hover:border-button-primary text-text-secondary hover:text-text-primary"
              title="Refresh device list"
            >
              ↻
            </button>
          </>
        )}

        {/* Upload mode: file picker inline */}
        {sourceMode === "file" && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 px-2 py-1 rounded-md border border-border-default hover:border-button-primary text-text-primary"
            >
              {fileName ? "Choose another file…" : "Choose file…"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handlePickFile}
              className="hidden"
            />
            {fileName && (
              <>
              <span
                className="flex-1 min-w-0 truncate text-text-secondary normal-case"
                title={fileName}
              >
                {fileName}
              </span>
              <button
                type="button"
                onClick={clearFile}
                className="shrink-0 px-2 py-1 rounded-md border border-border-default text-text-secondary hover:text-red-400"
                title="Remove file"
              >
                ×
              </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Video / status panel — 16:9 aspect so the recorded match
          footage (landscape) fills the box without big side gutters.
          Previously this was h-[clamp(140px,22vh,250px)] which produced
          a near-square container; with object-contain that left ~25% of
          the width as black bars on each side. aspect-video (16:9) eats
          those gutters and gives the operator a meaningfully larger
          video review surface in the same column. */}
      <div className="relative rounded-xl overflow-hidden bg-gradient-to-b from-black to-[#2A2A35] w-full aspect-video flex items-center justify-center">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          // defaultMuted (NOT `muted`) — sets the muted attribute once
          // at first mount so autoplay-with-audio bypass works, then
          // leaves the element's muted property under the operator's
          // control via the native <video controls> unmute button. The
          // controlled `muted` attribute re-applies on every React
          // re-render, silently re-muting after the operator clicks
          // unmute, which is why scorers couldn't get audio out.
          defaultMuted
          onTimeUpdate={(e) => {
            // Only meaningful in file mode — fires while scrubbing or playing.
            // BBB uses this to drive Clip in/out timestamps against the file
            // playhead instead of the live-recorder's wall-clock counter.
            if (sourceMode === "file") {
              onFileTimeUpdate?.(Math.round(e.currentTarget.currentTime * 1000));
            }
          }}
          className={`w-full h-full object-contain ${
            status === "live" ? "" : "opacity-0"
          }`}
        />

        {status !== "live" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 gap-2">
            <div
              className={`text-xs uppercase tracking-wider ${
                status === "error" ? "text-red-400" : "text-text-secondary"
              }`}
            >
              {sourceMode === "file" && !fileName && "Pick a recorded video file to begin"}
              {sourceMode === "device" && status === "idle" && "Initialising…"}
              {sourceMode === "device" && status === "requesting" && "Opening device…"}
              {status === "error" && "Video unavailable"}
            </div>
            {error && (
              <div className="text-[11px] text-text-secondary max-w-md">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Live overlay with device + resolution (device mode only) */}
        {sourceMode === "device" && status === "live" && trackInfo && (
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono">
            <span
              className={`px-1.5 py-0.5 rounded bg-black/60 backdrop-blur ${selectedTag.color}`}
            >
              ● {selectedTag.tag}
            </span>
            <span className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur text-text-primary">
              {trackInfo.width}×{trackInfo.height}
              {trackInfo.frameRate ? ` @ ${trackInfo.frameRate}fps` : ""}
            </span>
          </div>
        )}

        {/* File-mode overlay tag */}
        {sourceMode === "file" && status === "live" && (
          <div className="absolute top-2 left-2 text-[10px] font-mono">
            <span className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur text-violet-300">
              ● FILE
            </span>
          </div>
        )}
      </div>

      {/* Seek controls — file-mode only (live-stream seek is N/A).
          Buttons + step-size dropdown share state via seekStepSec, and
          the keyboard ←/→ handler above uses the same step (Chrome's
          native arrow seek can jump 2+ minutes on long videos — we
          intercept and force the operator's chosen step). */}
      {sourceMode === "file" && status === "live" && (
        <div className="flex items-center justify-center gap-2 mt-1">
          <button
            type="button"
            onClick={() => nudgeBy(-seekStepSec)}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-bg-secondary border border-border-default text-text-primary hover:border-button-primary/60 hover:bg-bg-main transition-colors"
            title={`Back ${seekStepSec} seconds`}
          >
            « {seekStepSec}s
          </button>
          <button
            type="button"
            onClick={() => nudgeBy(seekStepSec)}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-bg-secondary border border-border-default text-text-primary hover:border-button-primary/60 hover:bg-bg-main transition-colors"
            title={`Forward ${seekStepSec} seconds`}
          >
            {seekStepSec}s »
          </button>
          {/* Step-size selector — operator picks how far one click /
              one arrow-key press should jump. Persists to localStorage
              (zcd_video_seek_step). Compact width keeps the row tight. */}
          <select
            value={seekStepSec}
            onChange={(e) => setSeekStepSec(Number(e.target.value))}
            className="text-xs py-1 px-2"
            title="Forward / backward step size for the seek buttons and arrow keys"
          >
            {SEEK_STEPS.map((s) => (
              <option key={s} value={s}>{s}s</option>
            ))}
          </select>
        </div>
      )}

      {/* Diagnostic line — device-mode only. File-mode "Local file loaded
          ... Click ● Upload for clipping" copy removed per user request
          (it was eating vertical space the operator preferred for the
          video itself). Same info is implied by the file-name pill above. */}
      {sourceMode === "device" && status === "live" && trackInfo && (
        <div className="text-[10px] text-text-secondary font-mono truncate">
          {trackInfo.label}
        </div>
      )}
    </div>
  );
}

export default forwardRef(VideoPlayer);
